import { Recipe } from '@reduxjs/toolkit/dist/query/core/buildThunks';
import { createAuthorizationHeaderValue } from 'lib-shared-auth';
import { Writable } from 'ts-essentials';

import {
  ReadAiCheckGrammarAndSpellingApiResponse,
  ReadAiTextTranslationApiResponse,
  ReadAiWritingImprovementResponse,
} from '../types/aiApi.types';

import { getStreamCount, incrementStreamCount } from './streamCount';

type RequestBody = { [key: string]: unknown };
interface RequestConfig {
  url: string;
  body: RequestBody;
}

type StreamingResponse =
  | ReadAiCheckGrammarAndSpellingApiResponse
  | ReadAiTextTranslationApiResponse
  | ReadAiWritingImprovementResponse;

/**
 * Stream http calls using the url and body from requestConfig. Tries calling
 * the initial http call. If that fails, an error is emitted to the component.
 * If the initial http call is successful, a reader is initialized and continues
 * to read the values from the stream. From the reader, there is a property
 * called "done" which informs when the stream does not have anymore text. From
 * there, the component is informed that the streaming is done. If it so happens
 * that an error is encountered mid streaming, then an error is emitted to the
 * component. Note that the same error state is set for both the initial http
 * call failure and the "mid streaming failure", so from the consumer point of
 * view there is no differentiation between an initial http call failure and a
 * "mid streaming failure".
 */
export const streamUpdates = async (
  updateCachedData: (updateRecipe: Recipe<StreamingResponse>) => void,
  requestConfig: RequestConfig,
): Promise<void> => {
  const { url, body } = requestConfig;

  incrementStreamCount();
  const streamCountInitial = getStreamCount();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: await createAuthorizationHeaderValue(),
        Accept: 'text/plain',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      /**
       * If the initial http request fails, then transmit to the component that
       * an error has been encountered
       */
      updateCachedData((draft) => {
        (draft.meta.isLoading as Writable<boolean>) = false;
        (draft.meta.isError as Writable<boolean>) = true;
      });
      // Return immediately since the stream start up failed
      return;
    }

    try {
      // First chunk has been retrieved, set isLoading to false and isStreaming to true
      updateCachedData((draft) => {
        (draft.meta.isLoading as Writable<boolean>) = false;
        (draft.meta.isStreaming as Writable<boolean>) = true;
      });

      const reader = response?.body?.getReader();
      const decoder = new TextDecoder();

      while (true) {
        /**
         * We get into this if statement if the previous stream was cancelled by
         * the user. This can happen if the consumer component is unmounted
         * while text is still being received after calling the initial http
         * request.
         */
        if (streamCountInitial !== getStreamCount()) {
          updateCachedData((draft) => {
            /**
             * Before returning, we need to pass data to the consumer component
             * to reset the api to it's initial state, which means isLoading
             * equals true and isStreaming equals false.
             */
            (draft.meta.isLoading as Writable<boolean>) = true;
            (draft.meta.isStreaming as Writable<boolean>) = false;
            /**
             * Any text found here is from an earlier stream, so text needs to
             * be cleared by setting it to empty string.
             */
            (draft.text as Writable<string>) = '';
          });
          return;
        }

        const streamResult = await reader?.read();

        if (streamResult?.done) {
          updateCachedData((draft) => {
            (draft.meta.isStreaming as Writable<boolean>) = false;
            (draft.meta.isSuccess as Writable<boolean>) = true;
            (draft.feedback_id as Writable<string>) =
              response.headers.get('dixa-context-id') ?? '';
          });
          break;
        }

        if (streamResult?.value) {
          // Used to apply streaming updates as messages are received
          updateCachedData((draft) => {
            /**
             * Draft contains the current cache value and can be mutated Each
             * intercepted message can be used in in the actual component by
             * using useEffect and watching data
             */
            (draft.text as Writable<string>) += decoder.decode(
              streamResult.value,
            );
          });
        }
      }
    } catch (error) {
      // The process can also fail mid stream, then we need to propagate isError to the component
      updateCachedData((draft) => {
        (draft.meta.isLoading as Writable<boolean>) = false;
        (draft.meta.isError as Writable<boolean>) = true;
        (draft.feedback_id as Writable<string>) =
          response.headers.get('dixa-context-id') ?? '';
      });
    }
  } catch (error) {
    // fetch promise rejects when a network error is encountered
    updateCachedData((draft) => {
      (draft.meta.isLoading as Writable<boolean>) = false;
      (draft.meta.isError as Writable<boolean>) = true;
    });
  }
};
